import axios from 'axios';
import Cookies from 'js-cookie';
import { CONFIG } from '@/config/Config';
import { getAccountId, getUniId } from '@/core/utils/extractCookieId';

interface SaveGlobalFilterPayload {
  id: string; // Filter ID from encode response
  filter_hash: string; // SHA512 hash of the filter JSON string
}

interface SaveGlobalFilterResponse {
  status: string;
  code: number;
  data?: unknown;
  /**
   * The filter ID. If different from the sent filterId, it means the filter
   * already existed with this ID and was not saved as a new one.
   * Use this ID for subsequent operations with SavedFiltersService.
   */
  id?: string;
}

/**
 * Calculates SHA512 hash of a string using Web Crypto API
 */
const calculateSHA512 = async (data: string): Promise<string> => {
  const encoder = new TextEncoder();
  const buffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-512', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

class GlobalSavedFiltersService {
  private createAuthenticatedAxios() {
    const instance = axios.create({ timeout: 10000 });
    instance.interceptors.request.use((config) => {
      const token = Cookies.get(CONFIG.AUTH.SSID);
      const universeId = getUniId();
      const accountId = getAccountId();

      if (token) config.headers[CONFIG.AUTH.SSID] = token;
      if (universeId) config.headers['stilingue-universe-id'] = universeId;
      if (accountId) config.headers['stilingue-account-id'] = accountId;
      return config;
    });
    return instance;
  }

  private buildUrl(path: string): string {
    const gateway = CONFIG.getGatewayUrl();
    const version = CONFIG.getVersion();
    return gateway
      ? `${gateway}panels-service/${version}${path}`
      : `/panels-service/${version}${path}`;
  }

  /**
   * Saves a global filter with its hash after encode.
   * 
   * IMPORTANT: If the returned id differs from the sent filterId, it means
   * the filter already existed with that other ID and was NOT saved as new.
   * Always use the returned id (response.id) when saving a new filter with
   * SavedFiltersService, not the filterId parameter sent to this method.
   * 
   * @param filterJsonString - The JSON string of the filter (same as sent to encode)
   * @param filterId - The ID returned from encode endpoint
   * @returns Promise<SaveGlobalFilterResponse> - Response with id field (may differ from input)
   */
  async saveGlobalFilter(
    filterJsonString: string,
    filterId: string,
  ): Promise<SaveGlobalFilterResponse> {
    try {
      const filterHash = await calculateSHA512(filterJsonString);

      const account_id = getAccountId();
      const universe_id = getUniId();
      const url = this.buildUrl('/saved-filters/global-filter');
      const instance = this.createAuthenticatedAxios();

      const payload: SaveGlobalFilterPayload = {
        id: filterId,
        filter_hash: filterHash,
      };

      const response = await instance.post<SaveGlobalFilterResponse>(url, payload, {
        params: { account_id, universe_id },
      });
      return response.data;
    } catch (error) {
      console.error('Error saving global filter:', error);
      throw error;
    }
  }
}

const globalSavedFiltersService = new GlobalSavedFiltersService();
export default globalSavedFiltersService;
export { calculateSHA512 };
